import { BigDecimal } from "@solomon-labs/bigdecimal";

import type { UserUnstakingQueueEntry } from "@/stores/useStakingSessionStore";

export interface UsdvMigrationCooldownEntry {
    endsAt: Date;
    usdv: BigDecimal;
}

const ZERO = new BigDecimal(0);

export interface UsdvMigrationQueueLoadRequest {
    owner: string | null;
    generation: number;
}

/** True only when a response belongs to the latest wallet queue request. */
export function isCurrentUsdvMigrationQueueLoad(
    request: UsdvMigrationQueueLoadRequest,
    current: UsdvMigrationQueueLoadRequest
): boolean {
    return request.owner === current.owner && request.generation === current.generation;
}

export interface UsdvMigrationQueueSnapshot {
    /** Amount ready to claim, or the first pending entry when none is ready. */
    cooldownUsdv: BigDecimal;
    /** The earliest ready or pending queue timestamp. */
    cooldownEndsAt: Date;
    /** Every queue entry, retained for claim reconciliation. */
    cooldownEntries: UsdvMigrationCooldownEntry[];
}

/**
 * Converts every unstaking queue entry into the migration snapshot contract.
 * Matured amounts are one claimable total. Future entries stay separate so a
 * claim cannot hide a later cooldown.
 */
export function getUsdvMigrationQueueSnapshot(
    queue: UserUnstakingQueueEntry[] | undefined,
    nowSeconds = Math.floor(Date.now() / 1000)
): UsdvMigrationQueueSnapshot | null {
    if (!queue?.length) return null;

    const cooldownEntries = queue.map((entry) => ({
        endsAt: new Date(entry.timestamp * 1000),
        usdv: entry.amount
    }));
    const claimableEntries = queue.filter((entry) => entry.timestamp === 0 || entry.timestamp <= nowSeconds);
    const futureEntries = cooldownEntries.filter((entry) => entry.endsAt.getTime() > nowSeconds * 1000);
    const nextFutureEntry = futureEntries.reduce<UsdvMigrationCooldownEntry | null>(
        (earliest, entry) => (!earliest || entry.endsAt.getTime() < earliest.endsAt.getTime() ? entry : earliest),
        null
    );

    if (claimableEntries.length) {
        return {
            cooldownUsdv: claimableEntries.reduce((total, entry) => total.add(entry.amount), ZERO),
            cooldownEndsAt: new Date(Math.min(...claimableEntries.map((entry) => entry.timestamp * 1000))),
            cooldownEntries
        };
    }

    if (!nextFutureEntry?.usdv) return null;
    return {
        cooldownUsdv: nextFutureEntry.usdv,
        cooldownEndsAt: nextFutureEntry.endsAt,
        cooldownEntries
    };
}
